import { Settings, FlaskConical, Siren, Cpu, ShieldAlert, Users, ArrowRight } from "lucide-react";

const services = [
  {
    icon: Settings,
    title: "Security Architecture & Deployment",
    description: "End-to-end design and rollout of enterprise security platforms — EDR, SIEM, Identity Protection, and Cloud Security — tailored to your existing infrastructure and risk profile.",
  },
  {
    icon: FlaskConical,
    title: "PoC / PoV Execution",
    description: "Hands-on Proof of Concepts and technical validations for security platforms, so your team can make confident, informed decisions before committing to a deployment. 50+ PoCs delivered.",
  },
  {
    icon: Siren,
    title: "Incident Response & SOC Advisory",
    description: "Threat analysis, response planning, SOC process optimization, and Tier 2/Tier 3 coordination. Available for project-based or retainer engagements.",
  },
  {
    icon: Cpu,
    title: "Security Automation",
    description: "Custom scripts, playbooks, and integrations — Fusion, ServiceNow, Microsoft Teams — to reduce manual SOC workload and dramatically accelerate response times.",
  },
  {
    icon: ShieldAlert,
    title: "Compliance & DLP Strategy",
    description: "Policy design and enforcement for regulatory alignment — NIST, ISO 27001, and data protection standards — with hands-on implementation in Purview and SIEM platforms.",
  },
  {
    icon: Users,
    title: "Training & Workshops",
    description: "Live or remote training for L1/L2 SOC teams, security analysts, and leadership on CrowdStrike, Palo Alto, Microsoft Purview, threat hunting, and incident response best practices.",
  },
];

export default function Services() {
  return (
    <section id="services" className="py-20 md:py-32" data-testid="services-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-14">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#06B6D4]">Services</span>
          <h2 className="font-['Outfit'] text-3xl sm:text-4xl font-bold tracking-tight text-[#111827] mt-3">
            How I Can Help You
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((s) => (
            <div
              key={s.title}
              className="bg-white border border-[#E5E7EB] rounded-2xl p-6 md:p-8 card-hover group"
              data-testid={`service-card-${s.title.slice(0, 15).toLowerCase().replace(/\s+/g, '-')}`}
            >
              <div className="w-11 h-11 rounded-xl bg-[#0F172A]/5 flex items-center justify-center mb-5 group-hover:bg-[#06B6D4]/10 transition-colors">
                <s.icon className="w-5 h-5 text-[#0F172A] group-hover:text-[#06B6D4] transition-colors" />
              </div>
              <h3 className="font-['Outfit'] text-lg font-bold text-[#111827] mb-3">{s.title}</h3>
              <p className="text-sm text-[#6B7280] leading-relaxed mb-4">{s.description}</p>
              <button
                onClick={() => document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" })}
                className="inline-flex items-center text-sm font-medium text-[#06B6D4] hover:text-[#0596B5] transition-colors"
                data-testid={`service-cta-${s.title.slice(0, 10).toLowerCase().replace(/\s+/g, '-')}`}
              >
                Get in Touch <ArrowRight className="ml-1 w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>

        {/* Advisory callout */}
        <div className="mt-12 relative overflow-hidden rounded-2xl border border-[#E5E7EB] bg-white p-6 md:p-8" data-testid="advisory-callout">
          <div className="absolute inset-0 opacity-[0.03]">
            <img
              alt=""
              className="w-full h-full object-cover"
            />
          </div>
          <div className="relative flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <p className="text-[#6B7280] text-sm">
              For dedicated security consulting and advisory engagements, visit{" "}
              <a
                href="https://shiftleftsecure.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#06B6D4] font-medium hover:underline"
                data-testid="advisory-link"
              >
                shiftleftsecure.com <ArrowRight className="inline w-3.5 h-3.5" />
              </a>
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
